import { useCookies as useReactCookies } from 'react-cookie'
import { addYears, getUnixTime } from '@e3dc-react/shell/Libs/date'
import { isUndefined } from 'lodash'
import { useCallback, useMemo } from 'react'
import { AppTokens } from '@e3dc-react/shell/State/App/AppModel'

interface Cookies {
  googleAgreed?: boolean
  cookieConsent?: boolean
  invitationToken?: string
  companyToken?: string
  changeEmailToken?: string
  changeEmailAndUsernameToken?: string
  giftCodeToken?: string
  hideVehicleIntegrationHint?: boolean
  hideDashboardEditorInstructions?: boolean
  protectedFileToken?: string
}

interface UseCookiesHook {
  setGoogleAgreed: (agreed: boolean) => void
  setHideVehicleIntegrationHint: (hide: boolean) => void
  setConsentAgreed: (agreed: boolean) => void
  setTokens: (tokens: AppTokens) => void
  setHideDashboardEditorInstructions: (hide: boolean) => void
  removeTokens: () => void
  cookies: Cookies
}

enum CookieKeys {
  GoogleAgreed = 'google-agreed',
  CookieConsent = 'essential-agreed',
  InstallationInvitationToken = 'installation-invitation-token',
  CompanyInvitationToken = 'company-invitation-token',
  ChangeEmailConfirmationToken = 'change-email-token',
  ChangeEmailAndUsernameConfirmationToken = 'username-change-token',
  HideVehicleIntegrationHint = 'hide-vehicle-integration-hint',
  HideDashboardEditorInstructions = 'hide-dashboard-editor-instructions',
  GiftCodeToken = 'gift-code-token',
  ProtectedFileToken = 'protected-file-token',
}

/**
 * This value was chosen to have the cookies not expiring for a longer time period.
 * The cookies however are then not having additional five years of expiration.
 * It was not checked why is that happening.
 */
const COOKIE_LONG_EXPIRATION_YEAR_AMOUNT = 5

const longCookieOptions = { maxAge: getUnixTime(addYears(new Date(), COOKIE_LONG_EXPIRATION_YEAR_AMOUNT)), path: '/' }
const cookieOptions = { path: '/' }

/**
 * custom hook used to provide getter and setter functions for available cookies
 * TODO: remove cookie "HideVehicleIntegrationHint" in november, when the feature is planned to get removed
 */
const useCookies = (): UseCookiesHook => {
  const [cookies, setCookie, removeCookie] = useReactCookies([
    CookieKeys.ChangeEmailConfirmationToken,
    CookieKeys.ChangeEmailAndUsernameConfirmationToken,
    CookieKeys.GoogleAgreed,
    CookieKeys.CookieConsent,
    CookieKeys.CompanyInvitationToken,
    CookieKeys.InstallationInvitationToken,
    CookieKeys.GiftCodeToken,
    CookieKeys.HideVehicleIntegrationHint,
    CookieKeys.HideDashboardEditorInstructions,
    CookieKeys.ProtectedFileToken,
  ])
  const hideVehicleIntegrationHintCookie = cookies[CookieKeys.HideVehicleIntegrationHint]

  const setGoogleAgreed = (agreed: boolean): void => setCookie(CookieKeys.GoogleAgreed, `${agreed}`, longCookieOptions)
  const setConsentAgreed = (agreed: boolean): void => setCookie(CookieKeys.CookieConsent, `${agreed}`, longCookieOptions)
  const setHideVehicleIntegrationHint = (hide: boolean): void => setCookie(CookieKeys.HideVehicleIntegrationHint, `${hide}`, longCookieOptions)
  const setHideDashboardEditorInstructions = (hide: boolean): void =>
    setCookie(CookieKeys.HideDashboardEditorInstructions, `${hide}`, longCookieOptions)

  const cookieConsentValue = useMemo(
    () => (!isUndefined(cookies[CookieKeys.CookieConsent]) ? (cookies[CookieKeys.CookieConsent] === 'true' ? true : false) : undefined),
    [cookies]
  )

  const googleAgreedValue = useMemo(
    () => (!isUndefined(cookies[CookieKeys.GoogleAgreed]) ? (cookies[CookieKeys.GoogleAgreed] === 'true' ? true : false) : undefined),
    [cookies]
  )

  const hideVehicleIntegrationHintValue = useMemo(
    () => (!isUndefined(hideVehicleIntegrationHintCookie) ? hideVehicleIntegrationHintCookie === 'true' : undefined),
    [hideVehicleIntegrationHintCookie]
  )

  const setTokens = (tokens: AppTokens): void => {
    if (tokens.changeEmailToken) setCookie(CookieKeys.ChangeEmailConfirmationToken, tokens.changeEmailToken, cookieOptions)
    if (tokens.changeEmailAndUsernameToken)
      setCookie(CookieKeys.ChangeEmailAndUsernameConfirmationToken, tokens.changeEmailAndUsernameToken, cookieOptions)
    if (tokens.companyToken) setCookie(CookieKeys.CompanyInvitationToken, tokens.companyToken, cookieOptions)
    if (tokens.invitationToken) setCookie(CookieKeys.InstallationInvitationToken, tokens.invitationToken, cookieOptions)
    if (tokens.giftCodeToken) setCookie(CookieKeys.GiftCodeToken, tokens.giftCodeToken, cookieOptions)
    if (tokens.protectedFileToken) setCookie(CookieKeys.ProtectedFileToken, tokens.protectedFileToken, cookieOptions)
  }

  const removeTokens = useCallback((): void => {
    removeCookie(CookieKeys.ChangeEmailAndUsernameConfirmationToken, cookieOptions)
    removeCookie(CookieKeys.ChangeEmailConfirmationToken, cookieOptions)
    removeCookie(CookieKeys.CompanyInvitationToken, cookieOptions)
    removeCookie(CookieKeys.InstallationInvitationToken, cookieOptions)
    removeCookie(CookieKeys.GiftCodeToken, cookieOptions)
    removeCookie(CookieKeys.ProtectedFileToken, cookieOptions)
  }, [removeCookie])

  return {
    setGoogleAgreed,
    setConsentAgreed,
    setHideVehicleIntegrationHint,
    setTokens,
    setHideDashboardEditorInstructions,
    removeTokens,
    cookies: {
      cookieConsent: cookieConsentValue,
      googleAgreed: googleAgreedValue,
      hideVehicleIntegrationHint: hideVehicleIntegrationHintValue,
      changeEmailToken: cookies[CookieKeys.ChangeEmailConfirmationToken],
      changeEmailAndUsernameToken: cookies[CookieKeys.ChangeEmailAndUsernameConfirmationToken],
      companyToken: cookies[CookieKeys.CompanyInvitationToken],
      invitationToken: cookies[CookieKeys.InstallationInvitationToken],
      giftCodeToken: cookies[CookieKeys.GiftCodeToken],
      hideDashboardEditorInstructions: cookies[CookieKeys.HideDashboardEditorInstructions],
      protectedFileToken: cookies[CookieKeys.ProtectedFileToken],
    },
  }
}

export default useCookies
